import { Fragment, type ReactNode } from "react";

// A deliberately tiny inline **bold** parser, NOT a markdown library. Both MIRA
// (buyer workspace — the single key span of a question) and STERLING (talent
// platform — the opening message's section headers) are prompted to use ONLY
// markdown bold, never other markdown, so this splits on paired ** and wraps the
// inside in <strong>. Anything else (a lone *, an unmatched **, any other mark)
// is left as literal text, and we never use dangerouslySetInnerHTML — segments
// become real React nodes. Line breaks are left to the caller's CSS
// (white-space: pre-wrap), so a multi-line message with bold headers renders as
// expected.

export type InlineSegment = { text: string; bold: boolean };

// Non-greedy, requires at least one character between the markers, so `****`
// and a dangling `**` never match and pass through as literal text.
const BOLD_RE = /\*\*([\s\S]+?)\*\*/g;

// Pure + exported for unit testing: split text into bold / plain segments.
export function parseInlineBold(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(BOLD_RE)) {
    const start = match.index ?? 0;
    if (start > last) segments.push({ text: text.slice(last, start), bold: false });
    segments.push({ text: match[1], bold: true });
    last = start + match[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), bold: false });
  return segments;
}

// Render text with **bold** spans as <strong>. Fast-path the common case
// (nullish, or no markers) back to the raw input so an ordinary reply stays a
// plain text node. Accepts the same `string | undefined` a message's `text`
// field can be, so call sites drop in where a bare `{msg.text}` used to be.
export function renderInlineBold(text: string | null | undefined): ReactNode {
  if (text == null) return text;
  // Brief block payloads are untyped JSON on the server, so a numeric/other
  // value can reach here at runtime despite the declared type. Degrade it to
  // its string form — the pre-parser era rendered such values tolerantly, and
  // a crash in `.includes` would blank the whole block.
  const value = typeof text === "string" ? text : String(text);
  if (!value || !value.includes("**")) return value;
  return parseInlineBold(value).map((seg, i) =>
    seg.bold ? (
      <strong key={i}>{seg.text}</strong>
    ) : (
      <Fragment key={i}>{seg.text}</Fragment>
    ),
  );
}

// Plain-text form for attribute strings (aria-label and friends, which take a
// string, not React nodes): the same paired-** spans with the markers REMOVED
// rather than wrapped — assistive tech should hear the question, not
// "star star". Unpaired/stray asterisks pass through untouched, exactly like
// renderInlineBold. Nullish in → undefined out, so `aria-label=` omits the
// attribute the same way the bare value would.
export function stripInlineBold(text: string | null | undefined): string | undefined {
  if (text == null) return undefined;
  const value = typeof text === "string" ? text : String(text);
  if (!value.includes("**")) return value;
  return parseInlineBold(value)
    .map((seg) => seg.text)
    .join("");
}
